import Head from 'next/head';
import { renderMetaTags, useQuerySubscription } from 'react-datocms';
import { PageIntro, Layout, PersonSummary } from 'components';
import { request } from 'lib/datocms';
import { metaTagsFragment, responsiveImageFragment, menuFragment } from 'lib/fragments';

export async function getStaticPaths() {
  const data = await request({ query: `{ allTeamMembers(first: 100) { slug } }` });

  return {
    paths: data.allTeamMembers.map((page) => `/people/${page.slug}`),
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params, preview = false }) {
  const graphqlRequest = {
    query: `
      query TeamMemberBySlug($slug: String) {
        menu {
          ...menuFragment
        }
        teamMember(filter: {slug: {eq: $slug}}) {
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
          name
          image {
            responsiveImage {
              ...responsiveImageFragment
            }
          }
          contactDetailsImage {
            responsiveImage {
              ...responsiveImageFragment
            }
          }
          slug
          jobTitle
          bio(markdown: true)
          linkedIn
        }
      }

      ${metaTagsFragment}
      ${responsiveImageFragment}
      ${menuFragment}
    `,
    preview,
    variables: {
      slug: params.slug,
    },
  };

  const subscription = preview
    ? {
        ...graphqlRequest,
        initialData: await request(graphqlRequest),
        token: process.env.NEXT_DATOCMS_API_TOKEN,
      }
    : {
        enabled: false,
        initialData: await request(graphqlRequest),
      };

  if (!subscription.initialData.teamMember) {
    return {
      redirect: {
      destination: '/people',
    }
  }
  }

  return {
    props: {
      subscription,
    },
    revalidate: 120,
  };
}

export default function TeamMember({ subscription }) {
  const {
    data: { teamMember, menu },
  } = useQuerySubscription(subscription);

  const metaTags = teamMember.seo;

  return (
    <Layout preview={subscription.preview} menu={menu}>
      {metaTags && <Head>{renderMetaTags(metaTags)}</Head>}

      <PageIntro title={teamMember.name} introduction={teamMember.jobTitle} isPersonPage />

      {/* Pass image to actual person block component */}
      <section className="PageBody PageBody--people">
        <PersonSummary {...teamMember} />
      </section>
    </Layout>
  );
}
